import {
  CTA,
  Spinner,
  Text,
  TokenTextAppearance,
  styled,
} from '@volkswagen-onehub/components-core';
import React, {Dispatch, SetStateAction, useContext, useState} from 'react';
import {PagesContext} from '../../store/context/modules/PagesContext';
import {PAGES} from '../../types/enums/pages.enum';
import {Model} from '../../types/response';
import TimelineService from '../../services/TimelineService';
import {useAppDispatch} from '../../store/hooks';
import {set} from '../../store/slice/timelines.response';
import {Timeline} from '../../types/timeline';
import {Language} from '../../types/language';
import useTrans from '../../hooks/use-trans';
import {Vehicle} from '../../types/vehicles';
import {
  TrackingContext,
  TrackingEvents,
} from '../../store/context/modules/TrackingContext';
import {Button} from '../Button';
import {OptionType} from '../../types/select';
import SelectCmp from '../SelectCmp';
import {SingleValue} from 'react-select';
import { AppContext } from '../../store/context/AppContext';

interface FormInterface {
  model: Model;
  vehicle: Vehicle;
  setShowMaintainance: Dispatch<SetStateAction<boolean>>;
}

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const FormTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MainFormContainer = styled.div`
  margin-top: ${(props) => props.theme.size.dynamic0040};
  display: flex;
  flex-direction: column;
  row-gap: 2rem;
`;

const SelectContainer = styled.div`
  align-self: center;

  button {
    width: 150px;
  }
`;

const Form: React.FC<FormInterface> = ({
  model,
  vehicle,
  setShowMaintainance,
}) => {
  const appCtx = useContext(AppContext);
  const pageCtx = useContext(PagesContext);
  const trackCtx = useContext(TrackingContext);
  const dispatch = useAppDispatch();

  const [year, setYear] = useState<string | undefined>(undefined); // '2023'
  const [engine, setEngine] = useState<string | undefined>(undefined); // 'CA23UR,CA24UR,CA25UR'
  const [onRequest, setOnRequest] = useState<boolean>(false);
  const [disableButton, setDisableButton] = useState<boolean>(false);

  const trans = useTrans();

  const onYearSelect = (option: SingleValue<OptionType>) => {
    const curYearValue = option?.value;

    setYear(curYearValue);

    if (curYearValue != 'DEFAULT') {
      setShowMaintainance(false);
      setEngine('DEFAULT');
      setDisableButton(false);

      trackCtx?.init(
        TrackingEvents.FILTER_SELECT_CLICK,
        (vehicle.model as string).toUpperCase(),
        (vehicle.model as string).toUpperCase() + '-SELECT',
        {name: (vehicle.model as string).toUpperCase() + ' : SELECT : YEAR'},
        {
          type: 'single_value',
          name: 'year',
          values: [year],
        },
        null,
        {
          name:
            (vehicle.model as string).toLowerCase() + '-process-year-select',
          number: 3,
        },
      );
    } else {
      setShowMaintainance(false);
    }
  };

  const onEngineSelect = (v: SingleValue<OptionType>) => {
    const currentValue = v?.value;

    setEngine(currentValue);

    if (currentValue && currentValue != 'DEFAULT') {
      const engineParsed = JSON.parse(currentValue);

      setShowMaintainance(false);

      setDisableButton(false);

      trackCtx?.init(
        TrackingEvents.FILTER_SELECT_CLICK,
        (vehicle.model as string).toUpperCase(),
        (vehicle.model as string).toUpperCase() + '-SELECT',
        {name: (vehicle.model as string).toUpperCase() + ' : SELECT : ENGINE'},
        {
          type: 'single_value',
          name: 'engine',
          values: [engineParsed.engine],
        },
        null,
        {
          name:
            (vehicle.model as string).toLowerCase() + '-process-engine-select',
          number: 4,
        },
      );
    } else {
      setShowMaintainance(false);
    }
  };

  const renderYear = () => {
    const years: OptionType[] = Object.keys(model).map((k) => {
      return {
        value: k,
        label: k,
      };
    });

    return (
      <SelectCmp
        id="year"
        placeholder={trans.result_select}
        options={years}
        setValue={onYearSelect}
        label={trans.vehicle_year}
      />
    );
  };

  const renderEngine = () => {
    const engines = model[year as unknown as number];
    const enginesData: OptionType[] = Object.keys(engines).map((t) => {
      return {value: JSON.stringify({code: engines[t], engine: t}), label: t};
    });

    return (
      <SelectCmp
        id="engine"
        placeholder={trans.result_select}
        options={enginesData}
        setValue={onEngineSelect}
        label={trans.vehicle_engine}
      />
    );
  };

  const onSelect = async () => {
    if (year && engine) {
      setDisableButton(true);
      trackCtx?.init(
        TrackingEvents.FILTER_SEARCH_BUTTON_CLICK,
        (vehicle.model as string).toUpperCase(),
        (vehicle.model as string).toUpperCase() + '-SUBMIT',
        {name: (vehicle.model as string).toUpperCase() + ' : SUBMIT'},
        {
          results: [(vehicle.model as string).toUpperCase()],
          numberOfResults: 1,
          filterlist: [
            {
              type: 'multi_value',
              name: 'CG',
              values: [vehicle.model as string],
            },
            {
              type: 'single_value',
              name: 'ESV',
              values: [vehicle.model as string],
            },
          ],
        },
        null,
        {
          name: (vehicle.model as string).toLowerCase() + '-process-submit',
          number: 5,
        },
      );

      const engineParsed = JSON.parse(engine);

      setOnRequest(true);

      const timelineService = new TimelineService(appCtx.globalApi, year, engineParsed.code);
      const timelines = await timelineService.getTimelines();
      if (timelines.ok) {
        setOnRequest(false);
      }
      dispatch(set({value: timelines.parsedBody as Timeline<Language>}));
      setShowMaintainance(true);
    }
  };

  const onEdit = () => {
    pageCtx?.set(PAGES.SELECTION);

    trackCtx?.init(
      TrackingEvents.BUTTON_CLICK,
      (vehicle.model as string).toUpperCase(),
      (vehicle.model as string).toUpperCase() + '-EDIT-BUTTON',
      {name: (vehicle.model as string).toUpperCase() + ' EDIT BUTTON'},
      {numberOfResults: 1},
    );
  };

  return (
    <FormContainer>
      <FormTitleContainer>
        <Text appearance={TokenTextAppearance.headline300} bold>
          {trans['vehicles_' + vehicle.model]}
        </Text>
        <CTA
          emphasis="tertiary"
          tag="button"
          ariaLabel={trans.result_edit}
          onClick={() => onEdit()}
        >
          {trans.result_edit}
        </CTA>
      </FormTitleContainer>

      <MainFormContainer>
        {renderYear()}

        {year && year != 'DEFAULT' && renderEngine()}

        <SelectContainer>
          <Button
            ariaLabel="Select"
            emphasis="primary"
            disabled={
              !year || engine == 'DEFAULT' || onRequest || disableButton
            }
            onClick={onSelect}
          >
            {!onRequest ? trans.result_search : <Spinner />}
          </Button>
        </SelectContainer>
      </MainFormContainer>
    </FormContainer>
  );
};

export default Form;
